// A QUE PERSONA LE TOCA ESTE RESPONSABLE
//
// Esta decision se arreglo cuatro veces dentro de PlayerFormModal y salio rota las
// cuatro. La causa no fue ninguno de los cuatro bugs: fue que el alta estaba
// adivinando identidades y escribiendo en silencio sobre familias que nadie abrio.
// El modelo se replanteo para que el alta deje de decidir:
//
//   - Una familia tiene UN responsable. No hay dos candidatos peleando el puesto.
//   - Una persona que ya existe se REUSA, nunca se actualiza: corregirla es trabajo
//     de Pagos > Familias, que baja el cambio a `personas` por el trigger espejo.
//   - Cuando lo capturado es otra persona distinta a la que ya responde, se pregunta.
//     Nada se decide solo.
//
// Aqui viven las DOS preguntas, separadas a proposito:
//
//   resolverResponsable()         ¿QUIEN es esta persona?          usar | crear
//   responsableQueSeReemplaza()   ¿a quien destronaria ponerla?    saliente | null
//
// Ninguna de las dos escribe. No hay Supabase ni React: entran datos, sale una
// decision, y cada caminito tiene su test en __tests__/responsableResolver.test.js.

import { partirNombre } from './nombres'

export const soloDigitos = (t) => String(t || '').replace(/\D/g, '')

// Se exporta porque la BUSQUEDA tiene que comparar con la misma regla con la que se
// decide si dos nombres son el mismo. Si se separan, la busqueda encuentra a alguien
// que el resolver luego declara distinto -- o al reves.
export const normalizarTexto = (t) => String(t || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim().replace(/\s+/g, ' ')


// IDENTIDAD: "de quien estamos hablando". Sin acentos ni mayusculas, igual que
// apellidosDe(), que es con lo que se sugieren familias hermanas. En PROD 62 de las
// 136 personas (46%) estan guardadas en MAYUSCULAS o con acentos: comparar literal
// declaraba distinta a casi la mitad del padron.
export const mismoTexto = (a, b) => normalizarTexto(a) === normalizarTexto(b)

// VALOR: "quedo exactamente igual". Literal a proposito -- corregir "ANA RODRIGUEZ"
// a "Ana Rodriguez" es la misma persona (mismoTexto) pero SI es una edicion del
// responsable, y por eso tiene que pasar por la decision en vez de saltarsela. Con
// una sola comparacion, una de las dos preguntas sale mal.
export const mismoValor = (a, b) =>
  String(a || '').trim().replace(/\s+/g, ' ') === String(b || '').trim().replace(/\s+/g, ' ')

// partirNombre() devuelve ('','') cuando el texto era pura aclaracion: "Mama" a
// secas. En produccion hay 3 fichas asi, y con los dos campos vacios el capturista
// no ve lo que decia la ficha -- parece que nunca hubo responsable. Se siembra el
// texto original en Nombre(s) para que se vea que decia "Mama".
export const partirSinPerder = (texto) => {
  const partes = partirNombre(texto)
  if (partes.nombres || partes.apellidos) return partes
  return { nombres: String(texto || '').trim(), apellidos: '' }
}

const nombreDe = (p) =>
  p?.nombre_completo || `${p?.nombres || ''} ${p?.apellidos || ''}`.trim()

// La familia que hay que reusar cuando se reusa a la persona, y solo si es UNA. Con
// dos no hay nada que sugerir: cual de las dos seria adivinar. Viene de los vinculos
// que trae buscarPersonaPorTelefono(); una persona sin vinculos cargados no aporta
// nada aqui, y eso es lo correcto: no se afirma lo que no se sabe.
const familiaSugeridaDe = (persona) => {
  const ids = Array.from(new Set((persona?.familia_personas || [])
    .filter((v) => v && v.rol === 'responsable' && v.es_principal)
    .map((v) => v.familias?.id || v.familia_id || null)
    .filter(Boolean)))
  return ids.length === 1 ? ids[0] : null
}

// ¿El responsable quedo exactamente como lo traia la ficha?
//
// Gobierna dos cosas: no volver a tocar el modelo de personas cuando no hay nada que
// resolver, y no exigirle a una edicion requisitos que la ficha nunca cumplio (en
// PROD hay 28 responsables sin apellido y 16 sin telefono de 10 digitos).
//
// Se compara contra el nombre YA PARTIDO, no contra el texto crudo de la ficha. Es
// lo que el formulario le muestra al capturista: la ficha de Ximena Barajas dice
// "Jorge Barajas Papá" y el campo dice "Jorge Barajas", asi que comparar contra el
// crudo hacia que cambiar el dorsal contara como editar al responsable. En PROD son
// 5 fichas asi.
export function responsableIgualAlDeLaFicha({
  nombreCapturado, telefonoCapturado, emailCapturado,
  contactoFicha, telefonoFicha, emailFicha,
}) {
  const partes = partirSinPerder(contactoFicha)
  const nombreFicha = `${partes.nombres} ${partes.apellidos}`.trim()
  return mismoValor(nombreCapturado, nombreFicha)
    && soloDigitos(telefonoCapturado) === soloDigitos(telefonoFicha)
    && mismoValor(emailCapturado, emailFicha)
}

// PREGUNTA 1: ¿QUIEN es esta persona? Dos respuestas, y ninguna escribe sobre una
// persona que ya existe:
//
//   usar   ya existe: se usa su id tal cual, sin tocarle un solo campo
//   crear  nadie coincide: persona nueva
//
// Lo que esta funcion NO contesta es si hay que reemplazar al responsable que la
// familia ya tiene: esa es la PREGUNTA 2 y vive en hayQuePreguntarPorElReemplazo().
// Estuvieron enredadas en una sola respuesta y por ahi se colaba el Critico: cada
// camino que resolvia la identidad por su cuenta se saltaba el control.
export function resolverResponsable({
  personaLigada = null,
  principalActual = null,
  candidatosPorTelefono = [],
  nombreCapturado = '',
  telefonoCapturado = '',
  telefonoDescartado = '',
}) {
  const tel = soloDigitos(telefonoCapturado)
  const hayNombre = !!normalizarTexto(nombreCapturado)
  // El admin ya dijo "es otra persona" para este numero -- en la tarjeta de
  // candidatos o en la de conflicto: no se le vuelve a preguntar lo mismo, y ninguna
  // coincidencia de telefono cuenta.
  const descartado = !!tel && tel === soloDigitos(telefonoDescartado)

  // (a) La que el admin vio en la tarjeta y eligio. Es la unica evidencia que no hay
  //     que inferir: la puso un humano con los jugadores de la familia enfrente.
  if (personaLigada?.id) {
    return {
      accion: 'usar',
      personaId: personaLigada.id,
      familiaSugerida: familiaSugeridaDe(personaLigada),
      motivo: 'El admin confirmó en la tarjeta que es esta persona.',
    }
  }

  // (b) Alguien que ya existe y es, con evidencia, la misma persona:
  //      - quien ya responde por esta familia, si se llama igual ("el papa cambio de
  //        numero" es el caso mas comun que hay); o
  //      - alguien del padron con este mismo nombre. buscarPersonaPorTelefono() ya
  //        filtra por telefono exacto, asi que dentro de esa lista "mismo nombre" ya
  //        significa mismo nombre Y mismo telefono.
  //     Se reusa su id y su familia, y NO se le escribe nada: nadie confirmo que lo
  //     capturado ahora sea mas nuevo que lo que esa persona ya tenia.
  const conocidos = descartado
    ? [principalActual]
    : [principalActual, ...(candidatosPorTelefono || [])]
  const yaExiste = hayNombre
    ? conocidos.find((p) => p?.id && mismoTexto(nombreCapturado, nombreDe(p))) || null
    : null
  if (yaExiste) {
    return {
      accion: 'usar',
      personaId: yaExiste.id,
      familiaSugerida: familiaSugeridaDe(yaExiste),
      motivo: `${nombreDe(yaExiste)} ya existe: se usa su ficha tal como está.`,
    }
  }

  // (c) Cualquier otro caso. Compartir el telefono con alguien que se llama distinto
  //     no es ser esa persona: en PROD hay 4 familias donde papa y mama comparten
  //     numero (Jorge/ANA, JULIA/IRAZEMA, ELIANA/ELIZABETH, Isaac/Gerardo).
  return {
    accion: 'crear',
    personaId: null,
    familiaSugerida: null,
    motivo: 'Nadie coincide con lo capturado: es una persona nueva.',
  }
}


// PREGUNTA 2: ¿hay que reemplazar al responsable que esta familia YA tiene?
//
// Vive aparte de la pregunta 1 a proposito. Enredadas en una sola respuesta, cada
// camino que resolvia la identidad por su cuenta se saltaba el control y destronaba
// al responsable: la coincidencia por telefono (la rama `usar` ganaba a la de
// preguntar -- 8 pares expuestos en PROD), la tarjeta de candidatos (al admin se le
// pregunto "¿es la misma persona?", que no es "¿reemplazo al responsable?"), y
// cambiar de familia despues de ligar (el control miraba la familia vieja).
//
// Un solo control, evaluado contra la familia FINAL, cubre los tres. Importa porque
// ligar al nuevo destrona al anterior en el RPC, y el consentimiento de un hermano ya
// registrado se iba a un telefono nuevo sin que nadie abriera su ficha.
//
// Hubo un cuarto camino: la familia con el responsable capturado solo como texto en
// familias.tutor_* y sin vinculo. Esas columnas se retiraron (Fase 3) y el texto se
// convirtio en personas, asi que hoy el responsable de una familia ES su vinculo
// principal, y sin vinculo no hay responsable.
//
// Devuelve a QUIEN se destronaria (para poder nombrarlo en la tarjeta) o null si no
// hay nada que preguntar. La autorizacion se guarda con QUIEN sale, de QUE familia y
// POR QUIEN entra: si cualquiera de las tres cambia, se vuelve a preguntar.
export function responsableQueSeReemplaza({
  responsableActual = null,
  personaId = null,
  familiaId = null,
  nombreCapturado = '',
  autorizacion = null,
}) {
  const saliente = responsableActual?.id
    ? { id: responsableActual.id, nombre: nombreDe(responsableActual) }
    : null
  if (!saliente) return null

  // ¿Es la misma persona? Se compara por id, que es la verdad.
  if (saliente.id === personaId) return null

  const autorizado = !!autorizacion
    && autorizacion.familiaId === familiaId
    && (autorizacion.saliente?.id || null) === saliente.id
    && mismoTexto(autorizacion.saliente?.nombre, saliente.nombre)
    && mismoTexto(autorizacion.nombre, nombreCapturado)
  return autorizado ? null : saliente
}

// PREGUNTA 0, y va antes que las otras dos: ¿el jugador que responde por si mismo se
// esta sumando a una familia que YA tiene a OTRA persona respondiendo?
//
// Si es asi, no hay ninguna identidad que resolver ni nadie a quien reemplazar: entra
// como un hijo mas. La familia es de PAGOS, y quien paga sigue siendo quien ya estaba
// -- la mama de sus hermanos menores. El firma por si mismo igual que antes (eso sale
// de su edad, no de este vinculo).
//
// Sin esto, su propio nombre se tomaba como "el responsable capturado", la pregunta 2
// veia a la mama y ofrecia dos salidas: destronarla, o ponerla a ella como responsable
// de la hija. Ninguna era la correcta, y se eligio la primera (2026-09-08).
//
// Si quien ya responde ES el jugador (su propia familia), no es "otra persona" y el
// flujo normal sigue.
export function familiaTieneOtroResponsable({
  responsableActual = null,
  nombreCapturado = '',
}) {
  const nombre = responsableActual?.id ? nombreDe(responsableActual) : ''
  if (!nombre) return false
  return !mismoTexto(nombre, nombreCapturado)
}
